import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import CustomButton from "../../../Landing/component/Bouton/CustomButton";
import { useAppSelector } from "../../../globalState/hook";
import alert_message from "../../../Services/Alert/AlertMessage";
import AppAuthentication from "../../../Services/Authentication/AppAuthentication";
import translator from "../../Extra/Translation/Translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock } from "@fortawesome/free-solid-svg-icons";

import ReCAPTCHA from "react-google-recaptcha";
import { useVendo } from "../../../hooks/useVendo";
import { NavLink } from "react-router-dom";

interface AuthFormInputs {
    email: string;
    password: string;
    rememberMe: boolean;
}

const AuthComponent: React.FC = () => {
    const { authLoading } = useAppSelector((store) => store.auth);
    const { siginAsync } = AppAuthentication();
    const { translate } = translator();
    const [token, setToken] = useState<string | null>("toast");

    const { loading, connectionButton } = useVendo();

    const { register, handleSubmit } = useForm<AuthFormInputs>();

    const recaptchaRef = useRef<ReCAPTCHA | null>(null);

    const onSubmit = async (data: AuthFormInputs) => {
        if (token) {
            try {
                const promise = siginAsync(
                    data.email,
                    data.password,
                    token,
                    translate,
                    data.rememberMe,
                );
                alert_message.promise(
                    `${translate("Authentication", "Sign_in")}`,
                    promise,
                );
                recaptchaRef.current?.reset();
            } catch (error: any) {}
        } else {
            alert_message.error("Please verify that you are not a robot.");
        }
        setToken(null);
    };

    useEffect(() => {
        setTimeout(() => {
            recaptchaRef.current?.reset();
        }, 500);
    }, []);

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            className="bg-[#384146] auth-form opacity-90 shadow-lg z-10 rounded-xl px-4 md:!px-9 py-6 md:!py-8 space-y-6 w-full mx-auto"
        >
            <h3 className="text-sm text-white text-center mb-12">
                {translate("SignIn", "Sign_your_account")}
            </h3>
            <div className="relative">
                <div className="absolute !text-white inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faEnvelope} />
                </div>
                <input
                    type="email"
                    {...register("email", { required: true })}
                    className="focus:!bg-clisha-600 !text-white !border-transparent placeholder:!text-slate-300  !bg-clisha-600  w-full text-sm px-10 !py-3 !rounded-xl outline-clisha-600"
                    placeholder={translate("Register", "Email")}
                />
            </div>

            <div className="relative">
                <div className="absolute text-white inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faLock} />
                </div>
                <input
                    type="password"
                    {...register("password", { required: true })}
                    className="focus:!bg-clisha-600 !text-white !border-transparent placeholder:!text-slate-300  !bg-clisha-600  w-full text-sm px-10 !py-3 !rounded-xl outline-clisha-600"
                    placeholder={translate("Register", "Password")}
                />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 mt-6">
                <div className="flex items-center">
                    <input
                        type="checkbox"
                        {...register("rememberMe")}
                        className="size-4 shrink-0 !text-clisha-500 !border-clisha-500 focus:!ring-clisha-500 rounded"
                    />
                    <label
                        htmlFor="remember-me"
                        className="text-white ml-3 block text-xs"
                    >
                        {translate("SignIn", "Remember_me")}
                    </label>
                </div>
                <div>
                    <NavLink
                        to="/forgot-password"
                        className="text-clisha-500 font-semibold text-xs hover:underline"
                    >
                        {translate("SignIn", "Forgot_Password")}
                    </NavLink>
                </div>
            </div>

            <div className="my-6 flex items-center">
                <hr className="w-full !border-clisha-500" />
            </div>
            <div className="w-full justify-center recap">
                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            onChange={(token) => setToken(token)}
                        />
                        <input type="hidden" value={token ?? ""} name="token" />
                    </>
                )}
            </div>

            <div>
                <CustomButton
                    href="javascript:void(0);"
                    className="w-full text-white py-3 !border-transparent hover:!bg-clisha-500 !bg-clisha-500"
                    type="submit"
                    disabled={authLoading || loading}
                >
                    {translate("SignIn", "Sign_In")}
                </CustomButton>
            </div>
            {connectionButton({ className: "" })}
        </form>
    );
};

export default AuthComponent;
